import { useNavigate } from '@remix-run/react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Slide {
    id: number;
    headline: string;
    description: string;
    ctaText: string;
    ctaLink: string;
    productImage: string;
    backgroundColor: string;
}

const SLIDES: Slide[] = [
    {
        id: 1,
        headline: 'Unreal Price 12–16 Febuary',
        description: 'Your favorite groceries',
        ctaText: 'Explore now',
        ctaLink: '/search',
        productImage: '/crauser-bg-removed.png',
        backgroundColor: 'gradient-to-r from-[#E80245] to-[#820127]',
    },
    {
        id: 2,
        headline: 'Innovate your menu with us',
        description: 'Curated & created to fit your unique restaurant needs',
        ctaText: 'Explore now',
        ctaLink: '/search',
        productImage: 'https://redtab.xyz/wp-content/uploads/Redtab-Homepage-Marketplace-Large.jpg',
        backgroundColor: 'gradient-to-r from-[#E80245] to-[#820127]',
    },
];

export function HeroCarousel() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (isPaused) return;

        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
        }, 5000);

        return () => clearInterval(timer);
    }, [isPaused]);

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
    };

    const handleCTAClick = () => {
        navigate(SLIDES[currentIndex].ctaLink);
    };

    return (
        <div className="w-full">
            {/* Outer Container with Light Gray Background */}
            <div className="relative w-full rounded-[24px] border border-[#F2F2F2] p-6 gap-8 bg-white">

                {/* Inner Slider Container */}
                <div
                    className="relative w-full h-[180px] sm:h-[220px] md:h-[256px] overflow-hidden rounded-[20px] sm:rounded-[24px] group"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    {/* Background Gradient */}
                    <div
                        className="absolute inset-0 transition-all duration-700"
                        style={{ background: `linear-gradient(to right, #E80245, #820127)` }}
                    >
                        {/* Huge "RR" background patterns - Refined for depth */}
                        <div className="absolute inset-0 opacity-[0.08] pointer-events-none flex items-start justify-end select-none overflow-hidden pr-10">
                            <span className="text-[15rem] sm:text-[30rem] md:text-[60rem] font-bold text-white leading-none -mt-10 md:-mt-40 -mr-10 md:-mr-20 select-none">R</span>
                            <span className="text-[10rem] sm:text-[25rem] md:text-[50rem] font-bold text-white leading-none absolute top-[-5rem] md:top-[-10rem] right-[-5rem] md:right-[-15rem] opacity-50 select-none">R</span>
                        </div>
                    </div>

                    {/* Slider Content */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentIndex}
                            className="absolute inset-0 flex items-center justify-between pl-6 sm:pl-10 md:pl-20 overflow-hidden" // Changed padding and alignment
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                        >
                            {/* Left Content */}
                            <div className="relative z-10 flex flex-col justify-center max-w-[65%] sm:max-w-[70%] md:max-w-[75%]">
                                <h1
                                    className="text-white mb-2 md:mb-3 font-bold text-xl sm:text-2xl md:text-[39.56px] leading-tight md:leading-none tracking-tight"
                                    style={{ fontFamily: '"DM Sans", sans-serif' }}
                                >
                                    {SLIDES[currentIndex].headline}
                                </h1>
                                <p
                                    className="text-white/95 mb-4 md:mb-8 font-normal text-sm sm:text-lg md:text-4xl leading-tight md:leading-none tracking-tight"
                                    style={{ fontFamily: '"DM Sans", sans-serif' }}
                                >
                                    {SLIDES[currentIndex].description}
                                </p>
                                <button
                                    onClick={handleCTAClick}
                                    className="flex items-center justify-center transition-all duration-300 hover:opacity-90 hover:scale-105 active:scale-95 w-[100px] md:w-[124px] h-[32px] md:h-[41px] rounded-[40px] py-[5px] md:py-[7px] px-[10px] md:px-[14px] bg-white text-[#E80245] text-xs md:text-sm font-extrabold gap-1"
                                >
                                    {SLIDES[currentIndex].ctaText}
                                </button>
                            </div>

                            {/* Right Content (Product Image) */}
                            <div className="absolute right-0 bottom-0 h-full w-[40%] md:w-[45%] flex items-end justify-end">
                                <motion.img
                                    key={currentIndex + '-img'}
                                    src={SLIDES[currentIndex].productImage}
                                    alt="Product"
                                    className="h-[90%] md:h-[110%] w-auto object-contain object-bottom translate-y-[0%] md:translate-y-[5%]" // Increased size and push slightly down for flush look
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.6, delay: 0.1 }}
                                />
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Navigation Arrows - Precisely centered on the boundary between grey and maroon */}
                <button
                    onClick={handlePrevious}
                    className="absolute left-[-4px] sm:left-[-4px] top-1/2 -translate-y-1/2 z-20 flex items-center justify-center text-black transition-all outline-none hover:opacity-90 w-12 h-12 rounded-full p-2 gap-1 bg-white border border-black/10 shadow-xl"
                    aria-label="Previous slide"
                >
                    <ChevronLeft strokeWidth={2.5} className="w-6 h-6" />
                </button>
                <button
                    onClick={handleNext}
                    className="absolute right-[-4px] sm:right-[-4px] top-1/2 -translate-y-1/2 z-20 flex items-center justify-center text-black transition-all outline-none hover:opacity-90 w-12 h-12 rounded-full p-2 gap-1 bg-white border border-black/10 shadow-xl"
                    aria-label="Next slide"
                >
                    <ChevronRight strokeWidth={2.5} className="w-6 h-6" />
                </button>
            </div>
        </div>
    );
}
